import type { TFunction } from 'i18next';
import { ApiError } from './unwrapResponse';
import { getErrorMessage } from '../constants/errorCodes';

/**
 * Message of a thrown value, for a dialog that has to show why a mutation failed.
 *
 * The fallback is effectively unreachable — `unwrapResponse` always throws a real `Error` and
 * react-query rethrows it — but a dialog must never render "[object Object]" if that ever changes.
 */
export const errorMessage = (error: unknown, fallback = 'Unexpected error'): string =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * The sentence a **user** reads, and the only one a screen may render.
 *
 * It never falls back to the server's `message`, and that is the whole point. That string is
 * English, written for a log — « Issue not found on this service desk (HTTP 404) » was rendered in
 * a French drawer before this guard existed, and the doc comment two lines up had been claiming it
 * could not happen since the helper was written.
 *
 * The fallback is therefore **structural, not a convention**: no code, or a code nothing maps →
 * `getErrorMessage`'s own « Une erreur est survenue ». Worst case a screen loses precision; it can
 * no longer lose the reader. The fix for the loss of precision is to give the route an
 * `errorCode` — 102 of the 115 `sendError` calls in the backend still send none — not to reopen
 * this door.
 *
 * `errorMessage` stays exported for logs and Sentry, where the English is the useful part.
 */
export const apiErrorMessage = (t: TFunction, error: unknown): string =>
  getErrorMessage(t, error instanceof ApiError ? error.errorCode : null);

/** F18 — a failed save names its reason; the reason's trailing period is dropped so it does not double. */
export const saveErrorMessage = (t: TFunction, error: unknown): string =>
  t('Unable to save: {{reason}}. Your entries are kept.', { reason: apiErrorMessage(t, error).replace(/\.$/, '') });
